import React from "react";
import { IconButton, Menu, MenuItem, ListSubheader, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AlertTypeChip } from "./AlertTypeChip";

export const AlertDetailHeader = ({
  siteName,
  cameraName,
  siteHref,
  cameraHref,
  alertLabels,
  siteTime,
  seeMoreURL,
  canMute,
  isMuted,
  onMute,
  onDownloadVideo,
  canDownloadVideo,
  onSiteVideoWall,
  canShowSiteVideoWall,
  onSeeIgnoreZones,
  onSeeLineCrossings,
  showLineCrossings,
  talkDown,
}: {
  siteName: string;
  cameraName: string;
  siteHref?: string;
  cameraHref?: string;
  alertLabels?: string | null;
  siteTime: string;
  seeMoreURL: string;
  canMute: boolean;
  isMuted: boolean;
  onMute: () => void;
  onDownloadVideo: () => void;
  canDownloadVideo: boolean;
  onSiteVideoWall: () => void;
  canShowSiteVideoWall: boolean;
  onSeeIgnoreZones: () => void;
  onSeeLineCrossings: () => void;
  showLineCrossings: boolean;
  /** Talk-down control (ENG-2135); the control gates itself, so pass it
   *  whenever the row has a camera. Renders next to Mute. */
  talkDown?: React.ReactNode;
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleCopyAlertLink = async () => {
    if (!seeMoreURL) return;
    try {
      await navigator.clipboard.writeText(seeMoreURL);
    } catch {
      window.prompt("Copy alert link", seeMoreURL);
    }
    setMenuAnchor(null);
  };

  return (
    <div className="flex items-start justify-between gap-3 pb-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-1 font-mulish text-[14px] leading-tight">
          {siteHref ? (
            <Tooltip title="Open site page" placement="top">
              <a
                href={siteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[#1976d2] underline"
              >
                {siteName}
                <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
              </a>
            </Tooltip>
          ) : (
            <span className="text-actuate-blue">{siteName}</span>
          )}
          <span className="text-actuate-blue">/</span>
          {cameraHref ? (
            <Tooltip title="Open camera page" placement="top">
              <a
                href={cameraHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[#1976d2] underline"
              >
                {cameraName}
                <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
              </a>
            </Tooltip>
          ) : (
            <span className="text-actuate-blue">{cameraName}</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <AlertTypeChip alertLabels={alertLabels} />
          <span className="font-mulish text-[12px] text-actuate-blue">
            {siteTime}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => window.open(seeMoreURL, "_blank")}
          className="inline-flex h-9 items-center gap-1 whitespace-nowrap rounded bg-actuate-orange px-3 font-mulish text-[13px] font-bold text-white"
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
          See alert
        </button>
        {talkDown}
        {canMute && (
          <Tooltip
            title={
              isMuted
                ? "Alerts already muted for this site or camera."
                : "Mute alerts"
            }
            placement="top"
          >
            <span>
              <IconButton
                aria-label="Mute alerts"
                onClick={onMute}
                disabled={isMuted}
                size="small"
                sx={{ color: "#001943" }}
              >
                <NotificationsOffIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton
          aria-label="More alert actions"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          size="small"
          sx={{ color: "#001943" }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={() => setMenuAnchor(null)}
          MenuListProps={{ "aria-label": "Alert actions" }}
        >
          <ListSubheader className="font-mulish">Clip</ListSubheader>
          <MenuItem
            disabled={!canDownloadVideo}
            onClick={() => {
              onDownloadVideo();
              setMenuAnchor(null);
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            Download video
          </MenuItem>
          <MenuItem
            disabled={!canShowSiteVideoWall}
            onClick={() => {
              onSiteVideoWall();
              setMenuAnchor(null);
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            Site video wall
          </MenuItem>
          <ListSubheader className="font-mulish">Camera</ListSubheader>
          <MenuItem
            onClick={() => {
              onSeeIgnoreZones();
              setMenuAnchor(null);
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            See ignore zones
          </MenuItem>
          {showLineCrossings && (
            <MenuItem
              onClick={() => {
                onSeeLineCrossings();
                setMenuAnchor(null);
              }}
              sx={{ fontFamily: "Mulish" }}
            >
              See line crossings
            </MenuItem>
          )}
          <MenuItem onClick={handleCopyAlertLink} sx={{ fontFamily: "Mulish" }}>
            Copy alert link
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};
